import React, { Component } from 'react'
import { withRouter } from 'react-router-dom'
import { inject, observer } from 'mobx-react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import get from 'lodash/get'

import { formatMetricOpen } from '@atheneum/api/dist/lib'

import ApplicableContracts from './ApplicableContracts'
import ClientContacts from './ClientContacts'
import EPLQuickSearch from './EPLQuickSearch'
import ACL from '@components/ACL/ACL'
import Badge from '@components/Details/Badge'
import Header from '@components/Details/Header'
import PinIcon from '@components/Icon/Pin'
import ReloadIcon from '@components/Icon/Reload'
import StarIcon from '@components/Icon/Star'
import Tooltip from '@components/Tooltip'
import CopyButton from '../../../components/CopyButton'

class ProjectHeader extends Component {
  static propTypes = {
    projectStore: PropTypes.object,
    modalStore: PropTypes.object,
    activeProjectStore: PropTypes.object,
    history: PropTypes.object,
    match: PropTypes.object
  }

  constructor(props) {
    super(props)
    const {
      project,
      expertFeedbackStore,
      clientFeedbackStore,
      metricStore
    } = props.projectStore

    if (project && project.isOpen) {
      metricStore.load(project.id)
    } else if (project.isClosed) {
      expertFeedbackStore.load(project.id)
    }

    clientFeedbackStore.load(project.id)
  }

  handleRegenerateCode = () => {
    this.props.projectStore.regenerateCode(this.props.projectStore.project.id)
  }

  showClientFeedbacks = () => {
    const { modalStore, projectStore } = this.props
    const clients = projectStore.project.clients
    const feedbacks = projectStore.clientFeedbackStore.feedbacks

    return modalStore.show({
      type: 'CLIENT_FEEDBACK',
      props: {
        project: projectStore.project,
        clients,
        feedbacks
      }
    })
  }

  renderMetrics = () => {
    const { project } = this.props.projectStore

    if (project.isOpen) {
      return this.renderMetricOpen()
    } else if (project.isClosed) {
      return this.renderMetricClosed()
    }

    return null
  }

  renderMetricOpen = () => {
    const metric = get(this.props, 'projectStore.metricStore.metric')
    if (!metric) return null

    const metricData = formatMetricOpen({
      firstSubmit: metric.firstSubmit,
      lastSubmit: metric.lastSubmit,
      metricAverage: metric.average
    })

    return [
      <Badge
        key='1'
        label='Avg speed'
        value={metricData.average}
        tooltip='The average number of Experts submitted to Client per hour on this project today'
      />,
      <Badge
        key='2'
        label='Since last'
        value={metricData.lastDelivery}
        tooltip='Time since the last Expert for this project was submitted to Client today'
      />,
      <Badge
        key='3'
        label='1st expert'
        value={metricData.firstDelivery}
        tooltip='Elapsed time since 9:00 a.m. until the first expert was submitted to Client for this project today'
      />
    ]
  }

  renderMetricClosed = () => {
    const { clientFeedbackStore, expertFeedbackStore } = this.props.projectStore
    const redStarRange = clientFeedbackStore.averageRating < 5.01
    const orangeStarRange =
      clientFeedbackStore.averageRating > 5 &&
      clientFeedbackStore.averageRating < 7.5
    const greenStarRange = clientFeedbackStore.averageRating > 7.49
    const starClasses = classNames({
      'icon__fill--red': redStarRange,
      'icon__fill--orange': orangeStarRange,
      'icon__fill--green': greenStarRange
    })
    return [
      <Badge
        hasStar={!!clientFeedbackStore.averageRating}
        starClasses={starClasses}
        key='client-feedback'
        label='Client feedback'
        onClick={this.showClientFeedbacks}
        value={clientFeedbackStore.averageRating}
      />,
      <Badge
        key='expert-feedback'
        label='Expert feedback'
        value={expertFeedbackStore.averageRating}
      />
    ]
  }

  renderPin = () => {
    const { projectStore } = this.props
    const isActive = projectStore.project.bookmarks.find(bookmark => {
      return bookmark.projectId === projectStore.project.id
    })

    let pinClass = 'pin'
    let actionName = 'bookmark'
    if (isActive) {
      pinClass += ' pin--active'
      actionName = 'deleteBookmark'
    }

    return (
      <div
        className={pinClass}
        onClick={() => projectStore[actionName](projectStore.project.id)}
      >
        <PinIcon />
      </div>
    )
  }

  renderMcKinseyButtons = (project, capiDetails) => {
    const MC_KINSEY_PARENT_ACCOUNT_ID = 32
    if (project.parentAccountId !== MC_KINSEY_PARENT_ACCOUNT_ID) return null
    if (capiDetails) {
      return (
        <div
          style={{
            display: 'block',
            marginLeft: -20,
            marginBottom: 10,
            padding: '10px 20px',
            background: '#d2f3d3',
            fontWeight: '800',
            fontSize: 15,
            borderRadius: 6,
            width: 'fit-content'
          }}
        >
          {`This is a McKinsey Automation Project ${capiDetails.manual ? '(Email)' : '(API)'
            }`}
        </div>
      )
    } else {
      return (
        <div
          style={{
            display: 'block',
            marginLeft: -20,
            marginBottom: 10,
            padding: '10px 20px',
            background: '#ffffff',
            fontWeight: '800',
            fontSize: 15,
            borderRadius: 6,
            width: 'fit-content'
          }}
        >
          This is not a McKinsey Automation Project
        </div>
      )
    }
  }

  render() {
    const { project, capiDetails } = this.props.projectStore
    const {
      isActiveProjectLocked,
      activeProjectId,
      toggleLock
    } = this.props.activeProjectStore
    const { history, match } = this.props

    const urlMatch = match.url.match(/search/)
    const isLinkToFullPage = urlMatch && urlMatch.length > 0

    const isCurrentProjectLocked =
      isActiveProjectLocked && activeProjectId === project.id
    const activeIconClassName = isCurrentProjectLocked
      ? ''
      : ' active-project-icon'

    return (
      <Header>
        <div className='info info--noLeft'>
          <div>
            <div className='name-status-wrapper'>
              <h1
                className={isLinkToFullPage ? 'title-link' : ''}
                onClick={() =>
                  isLinkToFullPage && history.push(`/project/${project.id}`)
                }
              >
                {project.projectName}
              </h1>
              {this.renderPin()}
              <CopyButton value={project.projectName} />
              <Tooltip
                tooltip={
                  isCurrentProjectLocked
                    ? 'Your active project; click to deselect'
                    : 'Click to select as your active project'
                }
                placement='top'
              >
                <div className='iconWrapper'>
                  <StarIcon
                    className={'star-icon' + activeIconClassName}
                    onClick={e => {
                      e.preventDefault()
                      toggleLock(project.id, project.projectName)
                    }}
                  />
                </div>
              </Tooltip>
              {this.renderMcKinseyButtons(project, capiDetails)}
            </div>
            <span className='code-label'>
              {project.atheneumCode}
              <ACL roles={['Admin']} inline>
                <div onClick={this.handleRegenerateCode}>
                  <ReloadIcon />
                </div>
              </ACL>
            </span>
            <ClientContacts clients={project.clients} />
            <ApplicableContracts />
            <EPLQuickSearch />
          </div>
        </div>
        {this.renderMetrics()}
      </Header>
    )
  }
}

export default withRouter(
  inject('modalStore', 'activeProjectStore')(observer(ProjectHeader))
)
